import { useMemo, useState } from 'react';
import { parseBackendDateTime } from '../utils/persianNumber';

// Client-side sort+filter shared by every ticket list dashboard
// (UserDashboard's employee tab, TicketListPage, ReferredOutTicketsPage,
// DispatcherPage's queue) - they all already have the full ticket array in
// memory (see get_ticket_list_view's json shape: company/send_date on every
// row), so there's no need for extra API calls or backend params to sort/
// filter what's already loaded.
//
// `companyKey`/`dateKey` let each page point at whatever field name its own
// endpoint actually uses (get_ticket_list_view's rows use `company`/
// `send_date`, but not every list is built on that view) - see each page's
// call site.
export function useTicketFilters(tickets, { companyKey = 'company', dateKey = 'send_date' } = {}) {
  const [company, setCompany] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');

  // Combo box options come from whatever companies actually appear in this
  // list right now, not a separate company-list API call - matches the
  // request ("combo box from exist company name") and can never show a
  // company with zero tickets in it.
  const companyOptions = useMemo(() => {
    const set = new Set();
    tickets.forEach((t) => { if (t[companyKey]) set.add(t[companyKey]); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fa'));
  }, [tickets, companyKey]);

  const filteredSorted = useMemo(() => {
    let list = company ? tickets.filter((t) => t[companyKey] === company) : tickets;

    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return parseBackendDateTime(a[dateKey]) - parseBackendDateTime(b[dateKey]);
        case 'company_asc':
          return (a[companyKey] || '').localeCompare(b[companyKey] || '', 'fa');
        case 'priority_desc':
          return (b.priority || 0) - (a.priority || 0);
        case 'date_desc':
        default:
          return parseBackendDateTime(b[dateKey]) - parseBackendDateTime(a[dateKey]);
      }
    });

    return list;
  }, [tickets, company, sortBy, companyKey, dateKey]);

  return {
    filteredSorted,
    company, setCompany,
    sortBy, setSortBy,
    companyOptions,
    hasCompanyOptions: companyOptions.length > 0,
  };
}
